import type { ListedBlobRecord } from '@affine/nbstore';
import { Entity, LiveData } from '@toeverything/infra';
import { fileTypeFromBuffer } from 'file-type';

import { defaultWorkspaceApiClient } from '../../cloud/services/workspace-api-client';
import type { WorkspaceService } from '../../workspace';
import type { WorkspaceFlavoursService } from '../../workspace/services/flavours';

interface HydratedBlobRecord extends ListedBlobRecord, Disposable {
  url: string;
  extension?: string;
  type?: string;
  mime: string;
}

export class UnusedBlobs extends Entity {
  constructor(
    private readonly flavoursService: WorkspaceFlavoursService,
    private readonly workspaceService: WorkspaceService
  ) {
    super();
  }

  isLoading$ = new LiveData(false);
  state$ = new LiveData<'idle' | 'loading' | 'ready' | 'error'>('idle');
  error$ = new LiveData<Error | null>(null);
  hasMore$ = new LiveData(false);
  unusedBlobs$ = new LiveData<ListedBlobRecord[]>([]);
  private cursor: string | null = null;
  private abortController: AbortController | null = null;

  async revalidate(): Promise<void> {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.cursor = null;
    this.unusedBlobs$.setValue([]);
    await this.loadPage(true, this.abortController.signal);
  }

  async loadNextPage(): Promise<void> {
    if (!this.cursor || this.isLoading$.value) return;
    this.abortController = new AbortController();
    await this.loadPage(false, this.abortController.signal);
  }

  private get flavourProvider() {
    return this.flavoursService.flavours$.value.find(
      f => f.flavour === this.workspaceService.workspace.flavour
    );
  }

  async listBlobs() {
    const blobs = await this.flavourProvider?.listBlobs(
      this.workspaceService.workspace.id
    );
    return blobs;
  }

  async getBlob(blobKey: string) {
    const blob = await this.flavourProvider?.getWorkspaceBlob(
      this.workspaceService.workspace.id,
      blobKey
    );
    return blob;
  }

  async deleteBlob(blob: string, permanent: boolean) {
    if (!permanent) throw new Error('Unused files require permanent deletion.');
    const workspaceId = this.workspaceService.workspace.id;
    const response = await defaultWorkspaceApiClient.request({
      workspaceId,
      path: `/api/workspace/${workspaceId}/storage/unused-blobs/${encodeURIComponent(blob)}`,
      init: { method: 'DELETE' },
      category: 'settings',
      key: `DELETE:unused-blob:${blob}`,
      mode: 'foreground',
      priority: 'foreground',
    });
    const result = (await response.json()) as { deleted: boolean };
    if (!result.deleted) {
      throw new Error('The file is in use and was not deleted.');
    }
  }

  /**
   * Permanently remove a replaced content-addressed blob only after the
   * authoritative document sync and search index both confirm that no
   * attachment occurrence still references it.
   */
  async deleteBlobIfUnused(blob: string): Promise<boolean> {
    try {
      await this.deleteBlob(blob, true);
      return true;
    } catch {
      return false;
    }
  }

  async getUnusedBlobs(abortSignal?: AbortSignal) {
    const page = await this.fetchPage(null, abortSignal);
    return page.blobs;
  }

  private async loadPage(reset: boolean, signal: AbortSignal): Promise<void> {
    this.isLoading$.setValue(true);
    this.state$.setValue('loading');
    this.error$.setValue(null);
    try {
      const page = await this.fetchPage(reset ? null : this.cursor, signal);
      if (signal.aborted) return;
      this.unusedBlobs$.setValue(
        reset ? page.blobs : [...this.unusedBlobs$.value, ...page.blobs]
      );
      this.cursor = page.nextCursor;
      this.hasMore$.setValue(Boolean(page.nextCursor));
      this.state$.setValue('ready');
    } catch (error) {
      if (signal.aborted) return;
      this.error$.setValue(
        error instanceof Error ? error : new Error(String(error))
      );
      this.state$.setValue('error');
    } finally {
      if (!signal.aborted) this.isLoading$.setValue(false);
    }
  }

  private async fetchPage(cursor: string | null, signal?: AbortSignal) {
    const workspaceId = this.workspaceService.workspace.id;
    const params = new URLSearchParams({ limit: '50' });
    if (cursor) params.set('cursor', cursor);
    const response = await defaultWorkspaceApiClient.request({
      workspaceId,
      path: `/api/workspace/${workspaceId}/storage/unused-blobs?${params}`,
      init: { signal },
      category: 'settings',
      key: `GET:unused-blobs:${cursor ?? 'first'}`,
      mode: 'background',
      priority: 'visible',
    });
    return (await response.json()) as {
      blobs: ListedBlobRecord[];
      nextCursor: string | null;
    };
  }

  async hydrateBlob(
    record: ListedBlobRecord,
    abortSignal?: AbortSignal
  ): Promise<HydratedBlobRecord | null> {
    const blob = await this.getBlob(record.key);

    if (!blob || abortSignal?.aborted) {
      return null;
    }

    const fileType = await fileTypeFromBuffer(await blob.arrayBuffer());

    if (abortSignal?.aborted) {
      return null;
    }

    const mime = record.mime || fileType?.mime || 'unknown';
    const url = URL.createObjectURL(new Blob([blob], { type: mime }));
    // todo(@pengx17): the following may not be sufficient
    const extension = fileType?.ext;
    const type = extension ?? (mime?.startsWith('text/') ? 'txt' : 'unknown');
    return {
      ...record,
      url,
      extension,
      type,
      mime,
      [Symbol.dispose]: () => {
        URL.revokeObjectURL(url);
      },
    };
  }
}
